/**
 * The compliance-truth card, and the plain-English header sentence above it.
 *
 * Both read the rollup that now rides along on `/sites/:siteId/summary`, so
 * neither costs a round trip. The RULES live in `compliance-card.ts` and are
 * tested there; this file is presentation only.
 */
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { AlertTriangle, CheckCircle2, ShieldAlert } from "lucide-react";
import {
    allClearMessage,
    complianceRows,
    daysSince,
    overdueAge,
    type ComplianceRollup,
    type ComplianceRow,
    type ComplianceTone,
} from "./compliance-card";
import type { LocationTabId } from "./tabs";

/**
 * Tone classes name BOTH a surface and a foreground, and the base string
 * carries no `bg-*` at all. `cn()` is `twMerge(clsx())`, so a base with
 * `bg-card` plus a tone with `bg-destructive/10` makes twMerge DROP
 * `bg-card` — which is exactly how the OVERDUE tile went invisible in the
 * light theme (#1011, `stat-tile.tsx` has the full story and the test).
 */
const ROW_TONE: Record<ComplianceTone, string> = {
    danger: "border-destructive/40 bg-destructive/10 text-card-foreground",
    warn: "border-warning/40 bg-warning/10 text-card-foreground",
    info: "border-primary/30 bg-primary/5 text-card-foreground",
};

const ROW_ICON: Record<ComplianceTone, string> = {
    danger: "text-destructive",
    warn: "text-warning",
    info: "text-primary",
};

/** Where a row's single action goes: another tab, another page, or down the page. */
function RowAction({
    action,
    onAction,
}: {
    action: NonNullable<ComplianceRow["action"]>;
    onAction: (next: { tab: LocationTabId; trade?: string | null }) => void;
}) {
    const className = "shrink-0 text-xs font-semibold text-primary hover:underline";

    // An in-page anchor (`#site-tasks`) — the list is already on this page, so
    // scroll to it rather than navigating away from the location.
    if (action.href?.startsWith("#")) {
        const targetId = action.href.slice(1);
        return (
            <button
                type="button"
                className={className}
                onClick={() => {
                    const el = document.getElementById(targetId);
                    el?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
            >
                {action.label}
            </button>
        );
    }

    if (action.href) {
        return (
            <Link to={action.href} className={className}>
                {action.label}
            </Link>
        );
    }

    return (
        <button
            type="button"
            onClick={() => onAction({ tab: (action.tab ?? "devices") as LocationTabId, trade: action.trade ?? null })}
            className={className}
        >
            {action.label}
        </button>
    );
}

export function ComplianceCard({
    rollup,
    onAction,
}: {
    rollup: ComplianceRollup | null;
    onAction: (next: { tab: LocationTabId; trade?: string | null }) => void;
}) {
    const rows = complianceRows(rollup, new Date());

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                    {rows.length === 0 ? (
                        <CheckCircle2 className="h-4 w-4 text-success" />
                    ) : (
                        <ShieldAlert className="h-4 w-4 text-destructive" />
                    )}
                    Compliance
                </CardTitle>
            </CardHeader>
            <CardContent>
                {rows.length === 0 ? (
                    <p className="text-sm text-foreground/80">{allClearMessage(rollup)}</p>
                ) : (
                    <ul className="space-y-2">
                        {rows.map((r) => (
                            <li
                                key={r.id}
                                className={cn(
                                    "flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2",
                                    ROW_TONE[r.tone],
                                )}
                            >
                                <AlertTriangle className={cn("h-4 w-4 shrink-0", ROW_ICON[r.tone])} />
                                <span className="min-w-0 flex-1 text-sm font-medium">
                                    {r.text}
                                    {r.detail && (
                                        <span className="ml-1.5 font-normal text-foreground/75">— {r.detail}</span>
                                    )}
                                </span>
                                {/* Exactly one action per row. A row with three
                                    buttons is a row nobody clicks.

                                    Three destinations, and the `href` cases are
                                    NOT decoration: before #1043 this rendered a
                                    tab-switch button unconditionally with a
                                    `?? "devices"` fallback, so any row that
                                    wanted to leave the page would have silently
                                    opened the Devices tab instead. */}
                                {r.action && <RowAction action={r.action} onAction={onAction} />}
                            </li>
                        ))}
                    </ul>
                )}
            </CardContent>
        </Card>
    );
}

/**
 * The header sentence: what this location is, in one paragraph, derived
 * entirely from the rollup.
 *
 * A CSR picking up the phone should not have to read four cards to answer
 * "what's the story with this address". Everything here is a fact the page
 * already loaded — this just says it in order.
 */
export function LocationHeadline({ rollup, siteId }: { rollup: ComplianceRollup | null; siteId: string }) {
    if (!rollup) return null;

    const bits: string[] = [];
    bits.push(
        rollup.device_count === 0
            ? "No devices on record here"
            : `${rollup.device_count} device${rollup.device_count === 1 ? "" : "s"} on record`,
    );

    if (rollup.overdue_count > 0) {
        const days = daysSince(rollup.oldest_overdue_date, new Date());
        bits.push(
            days !== null && days > 0
                ? `${rollup.overdue_count} overdue, the oldest by ${overdueAge(days)}`
                : `${rollup.overdue_count} overdue`,
        );
    }
    if (rollup.failed_not_retested_count > 0) {
        bits.push(`${rollup.failed_not_retested_count} failed and never retested`);
    }
    if (rollup.missing_due_count > 0) bits.push(`${rollup.missing_due_count} with no due date at all`);
    if (rollup.scheduled_job_count > 0) bits.push(`${rollup.scheduled_job_count} job scheduled`);
    else if (rollup.open_job_count > 0) bits.push(`${rollup.open_job_count} job open but unscheduled`);
    if (rollup.approved_unscheduled_count > 0) {
        bits.push(`${rollup.approved_unscheduled_count} approved estimate waiting to be scheduled`);
    }
    if (rollup.portal_account_missing) bits.push("no portal account number, so nothing here can be filed");
    if (rollup.never_visited) {
        // Say which system's record this rests on. "Never visited" as a
        // bare claim reads as "nobody has ever been", which on an imported
        // book of business is usually false.
        bits.push(rollup.imported_only ? "imported and never worked here" : "never visited by us");
    }

    return (
        <p className="text-sm text-foreground/85">
            {bits.join(" · ")}.{" "}
            {rollup.unpaid_invoice_count > 0 && (
                <Link to={`/sites/${siteId}?tab=invoices`} className="font-medium text-warning hover:underline">
                    {rollup.unpaid_invoice_count} unpaid invoice
                    {rollup.unpaid_invoice_count === 1 ? "" : "s"}.
                </Link>
            )}
        </p>
    );
}

export function OverdueBadge({ count }: { count: number }) {
    if (count <= 0) return null;
    return (
        <Badge variant="outline" className="border-destructive/40 bg-destructive/10 text-[10px] text-destructive">
            {count} overdue
        </Badge>
    );
}
